import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Paper,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Button,
  Grid,
  IconButton,
  Tooltip,
  Snackbar,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import axios from "axios";
import StockPriceChart from "./StockPriceChart";

const darkTheme = createTheme({
  palette: {
    mode: "dark",
    primary: {
      main: "#00ff00",
    },
    secondary: {
      main: "#00bfff",
    },
    background: {
      default: "#0a0a0a",
      paper: "#1a1a1a",
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      color: "#00ff00",
      fontWeight: "bold",
    },
    h6: {
      color: "#00bfff",
    },
  },
});

function StockPage() {
  const { ticker } = useParams();
  const navigate = useNavigate();
  const [stockInfo, setStockInfo] = useState(null);
  const [reports, setReports] = useState([]);
  const [loadingInfo, setLoadingInfo] = useState(true);
  const [loadingReports, setLoadingReports] = useState(true);
  const [errorInfo, setErrorInfo] = useState(null);
  const [errorReports, setErrorReports] = useState(null);
  const [selectedReports, setSelectedReports] = useState([]);
  const [sortOrder, setSortOrder] = useState("desc");
  const [analyzing, setAnalyzing] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const fetchStockInfo = useCallback(async () => {
    setLoadingInfo(true);
    setErrorInfo(null);
    try {
      const response = await axios.get(`/api/stock/${ticker}/info`);
      if (response.data.success) {
        setStockInfo(response.data.data);
      } else {
        setErrorInfo(response.data.error || "Failed to load stock info");
      }
    } catch (err) {
      setErrorInfo(err.response?.data?.error || "Failed to load stock info");
    } finally {
      setLoadingInfo(false);
    }
  }, [ticker]);

  const fetchReports = useCallback(async () => {
    setLoadingReports(true);
    setErrorReports(null);
    try {
      const response = await axios.get(`/api/stock/${ticker}/reports`);
      if (response.data.success) {
        setReports(response.data.data);
      } else {
        setErrorReports(response.data.error || "Failed to load reports");
      }
    } catch (err) {
      setErrorReports(err.response?.data?.error || "Failed to load reports");
    } finally {
      setLoadingReports(false);
    }
  }, [ticker]);

  useEffect(() => {
    if (ticker) {
      fetchStockInfo();
      fetchReports();
    }
  }, [ticker, fetchStockInfo, fetchReports]);

  const formatCurrency = (val) => {
    if (val === null || val === undefined) return "-";
    return `$${Number(val).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatMarketCap = (val) => {
    if (!val) return "-";
    if (val >= 1e12) return `$${(val / 1e12).toFixed(2)}T`;
    if (val >= 1e9) return `$${(val / 1e9).toFixed(2)}B`;
    if (val >= 1e6) return `$${(val / 1e6).toFixed(2)}M`;
    return `$${val.toLocaleString()}`;
  };

  const formatVolume = (val) => {
    if (!val) return "-";
    if (val >= 1e6) return `${(val / 1e6).toFixed(2)}M`;
    if (val >= 1e3) return `${(val / 1e3).toFixed(1)}K`;
    return val.toLocaleString();
  };

  const formatPercent = (val) => {
    if (val === null || val === undefined) return "-";
    return `${(val * 100).toFixed(2)}%`;
  };

  const formatNumber = (val, decimals = 2) => {
    if (val === null || val === undefined) return "-";
    return Number(val).toFixed(decimals);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getDecisionChip = (decision) => {
    if (!decision) return <Chip label="N/A" size="small" />;
    const colorMap = {
      BUY: { color: "success" },
      SELL: { color: "error" },
      HOLD: { color: "warning" },
      OUTPERFORM: { sx: { bgcolor: "#81c784", color: "#1b5e20" } },
      UNDERPERFORM: { sx: { bgcolor: "#ef9a9a", color: "#b71c1c" } },
    };
    const config = colorMap[decision] || { color: "default" };
    return (
      <Chip
        label={decision}
        color={config.color}
        size="small"
        sx={{ fontWeight: "bold", ...config.sx }}
      />
    );
  };

  const handleSelectReport = (reportId) => {
    setSelectedReports((prev) => {
      if (prev.includes(reportId)) {
        return prev.filter((id) => id !== reportId);
      }
      if (prev.length >= 2) {
        return [prev[1], reportId];
      }
      return [...prev, reportId];
    });
  };

  const handleCompare = () => {
    if (selectedReports.length === 2) {
      navigate(`/?compare=${selectedReports[0]}&compare2=${selectedReports[1]}&ticker=${ticker}`);
    }
  };

  const handleAnalyze = async () => {
    setAnalyzing(true);
    try {
      const response = await axios.post("/api/analyze", {
        ticker,
        analysis_date: new Date().toISOString().split("T")[0],
      });
      if (response.data.success) {
        setSnackbar({ open: true, message: `Analysis queued for ${ticker}`, severity: "success" });
      } else {
        setSnackbar({ open: true, message: response.data.error || "Failed to start analysis", severity: "error" });
      }
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || "Failed to start analysis", severity: "error" });
    } finally {
      setAnalyzing(false);
    }
  };

  const toggleSortOrder = () => {
    setSortOrder((prev) => (prev === "desc" ? "asc" : "desc"));
  };

  const sortedReports = [...reports].sort((a, b) => {
    const dateA = new Date(a.created_at || 0);
    const dateB = new Date(b.created_at || 0);
    return sortOrder === "desc" ? dateB - dateA : dateA - dateB;
  });

  const statItems = stockInfo && stockInfo.found ? [
    { label: "Market Cap", value: formatMarketCap(stockInfo.marketCap) },
    { label: "P/E (TTM)", value: formatNumber(stockInfo.trailingPE) },
    { label: "P/E (Fwd)", value: formatNumber(stockInfo.forwardPE) },
    { label: "EPS", value: formatCurrency(stockInfo.eps) },
    { label: "52W High", value: formatCurrency(stockInfo.fiftyTwoWeekHigh) },
    { label: "52W Low", value: formatCurrency(stockInfo.fiftyTwoWeekLow) },
    { label: "Day Range", value: stockInfo.dayLow && stockInfo.dayHigh ? `${formatCurrency(stockInfo.dayLow)} - ${formatCurrency(stockInfo.dayHigh)}` : "-" },
    { label: "Volume", value: formatVolume(stockInfo.volume) },
    { label: "Avg Volume", value: formatVolume(stockInfo.avgVolume) },
    { label: "Beta", value: formatNumber(stockInfo.beta) },
    { label: "Dividend Yield", value: stockInfo.dividendYield != null ? `${Number(stockInfo.dividendYield).toFixed(2)}%` : "-" },
    { label: "Sector", value: stockInfo.sector || "-" },
    { label: "Industry", value: stockInfo.industry || "-" },
    { label: "Exchange", value: stockInfo.exchange || "-" },
  ] : [];

  return (
    <ThemeProvider theme={darkTheme}>
      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: "background.default",
          py: 4,
        }}
      >
        <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 2 } }}>
          <Paper
            elevation={3}
            sx={{ p: { xs: 1.5, sm: 2, md: 4 }, backgroundColor: "background.paper" }}
          >
            <Box display="flex" alignItems="center" mb={3}>
              <IconButton onClick={() => navigate('/history')} sx={{ mr: 1, color: "primary.main" }}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h4" gutterBottom sx={{ mb: 0 }}>
                {ticker}
              </Typography>
              {!loadingInfo && stockInfo && stockInfo.found && (
                <Typography variant="h6" sx={{ ml: 2, color: "text.secondary" }}>
                  {stockInfo.name}
                </Typography>
              )}
            </Box>

            {loadingInfo ? (
              <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress color="primary" />
              </Box>
            ) : errorInfo ? (
              <Alert severity="error" sx={{ mb: 3 }}>
                {errorInfo}
              </Alert>
            ) : stockInfo && stockInfo.found ? (
              <Paper sx={{ p: 3, mb: 4, backgroundColor: "rgba(0, 191, 255, 0.05)", border: "1px solid", borderColor: "divider" }}>
                <Box display="flex" alignItems="baseline" gap={2} mb={3}>
                  <Typography variant="h3" sx={{ color: "primary.main", fontWeight: "bold" }}>
                    {formatCurrency(stockInfo.currentPrice)}
                  </Typography>
                  {stockInfo.previousClose && stockInfo.currentPrice && (
                    <Typography
                      variant="h6"
                      sx={{
                        color: stockInfo.currentPrice >= stockInfo.previousClose ? "success.main" : "error.main",
                      }}
                    >
                      {stockInfo.currentPrice >= stockInfo.previousClose ? "+" : ""}
                      {(stockInfo.currentPrice - stockInfo.previousClose).toFixed(2)} (
                      {((stockInfo.currentPrice - stockInfo.previousClose) / stockInfo.previousClose * 100).toFixed(2)}%)
                    </Typography>
                  )}
                  {stockInfo.currency && (
                    <Typography variant="body2" color="text.secondary">
                      {stockInfo.currency}
                    </Typography>
                  )}
                </Box>

                <Grid container spacing={2}>
                  {statItems.map((item, idx) => (
                    <Grid item xs={6} sm={4} md={3} key={idx}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          {item.label}
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {item.value}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            ) : (
              <Alert severity="info" sx={{ mb: 3 }}>
                No stock information found for {ticker}. Reports may still be available below.
              </Alert>
            )}

            {!loadingReports && reports.length > 0 && (
              <Paper sx={{ p: 3, mb: 4, backgroundColor: "rgba(0, 191, 255, 0.03)", border: "1px solid", borderColor: "divider" }}>
                <StockPriceChart ticker={ticker} />
              </Paper>
            )}

            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h5" color="primary">
                Past Analysis Reports
              </Typography>
              <Box display="flex" gap={2} alignItems="center">
                {selectedReports.length === 2 && (
                  <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<CompareArrowsIcon />}
                    onClick={handleCompare}
                  >
                    Compare Selected
                  </Button>
                )}
                {selectedReports.length > 0 && selectedReports.length < 2 && (
                  <Typography variant="caption" color="text.secondary">
                    Select 1 more report to compare
                  </Typography>
                )}
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={analyzing ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
                  onClick={handleAnalyze}
                  disabled={analyzing}
                >
                  {analyzing ? "Queuing..." : "New Analysis"}
                </Button>
              </Box>
            </Box>

            {errorReports && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {errorReports}
              </Alert>
            )}

            <TableContainer component={Paper} sx={{ backgroundColor: "background.paper" }}>
              {loadingReports ? (
                <Box display="flex" justifyContent="center" p={4}>
                  <CircularProgress color="primary" />
                </Box>
              ) : reports.length === 0 ? (
                <Box display="flex" justifyContent="center" p={4}>
                  <Typography color="textSecondary">
                    No analysis reports found for {ticker}.
                  </Typography>
                </Box>
              ) : (
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" />
                      <TableCell
                        sx={{ color: "primary.main", fontWeight: "bold", cursor: "pointer", userSelect: "none" }}
                        onClick={toggleSortOrder}
                      >
                        Date {sortOrder === "desc" ? "↓" : "↑"}
                      </TableCell>
                      <TableCell sx={{ color: "primary.main", fontWeight: "bold" }}>Decision</TableCell>
                      <TableCell sx={{ color: "primary.main", fontWeight: "bold" }}>Confidence</TableCell>
                      <TableCell sx={{ color: "primary.main", fontWeight: "bold" }}>Agent</TableCell>
                      <TableCell sx={{ color: "primary.main", fontWeight: "bold" }}>Model</TableCell>
                      <TableCell sx={{ color: "primary.main", fontWeight: "bold" }} align="right">Report</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedReports.map((report) => (
                      <TableRow
                        key={report.id}
                        hover
                        selected={selectedReports.includes(report.id)}
                        sx={{
                          cursor: "pointer",
                          "&.Mui-selected": {
                            backgroundColor: "rgba(0, 255, 0, 0.1)",
                          },
                          "&.Mui-selected:hover": {
                            backgroundColor: "rgba(0, 255, 0, 0.15)",
                          },
                          "&:hover": {
                            backgroundColor: "rgba(0, 255, 0, 0.05)",
                          },
                        }}
                        onClick={() => handleSelectReport(report.id)}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedReports.includes(report.id)}
                            color="primary"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {report.analysis_date || "-"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(report.created_at)}
                          </Typography>
                        </TableCell>
                        <TableCell>{getDecisionChip(report.final_decision)}</TableCell>
                        <TableCell>
                          {report.confidence_score != null ? (
                            <Typography variant="body2">
                              {(report.confidence_score * 100).toFixed(0)}%
                            </Typography>
                          ) : (
                            "-"
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {report.agent_type || "-"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {report.llm_model || "-"}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                          {report.job_id && (
                            <Tooltip title="View Report">
                              <IconButton
                                size="small"
                                color="secondary"
                                onClick={() => navigate(`/reports/${report.job_id}`)}
                              >
                                <OpenInNewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </TableContainer>

            <Box
              textAlign="center"
              mt={4}
              pt={3}
              borderTop="1px solid rgba(255,255,255,0.1)"
            >
              <Typography variant="caption" color="textSecondary">
                Powered by GPT
              </Typography>
            </Box>
          </Paper>

          <Snackbar
            open={snackbar.open}
            autoHideDuration={4000}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          >
            <Alert
              onClose={() => setSnackbar({ ...snackbar, open: false })}
              severity={snackbar.severity}
              sx={{ width: "100%" }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Container>
      </Box>
    </ThemeProvider>
  );
}

export default StockPage;
